import { useEffect, useRef, useState } from "react";

import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { enumBGAreas } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/BackgroundAreas";
import { useTopic } from "@/hooks/useTopic";
import { QPicDesc } from "@coreapp/CoreApp/QPicDesc";
import { QBackgroundArea } from "@coreapp/CoreApp/QBackgroundArea";
import { enumBoxSubTypes } from "@coreapp/constants";

const getActiveBackgroundArea = () => {
  const wsMgr = GetWSMgr();
  const backgroundMgr = wsMgr?.fBackgroundMgr;
  const activeBackground = backgroundMgr?.fActiveBackgroundArea?.Clone();
  return activeBackground?.GetAreas() ?? {};
};

export const useBackgroundController = props => {
  const wsMgr = GetWSMgr();
  const backgroundMgr = wsMgr?.fBackgroundMgr;
  const docMgr = wsMgr?.GetDocMgr();
  const curDoc = docMgr?.GetCurrentDoc();
  const [backgroundArea, setBackgroundArea] = useState(getActiveBackgroundArea);

  const backgroundAreaRef = useRef(null);
  backgroundAreaRef.current = backgroundArea;

  useTopic("DropBackgroundPhoto", ({ pRef }) => {
    handleDropPhoto(pRef);
  });

  useEffect(() => {
    const wsMgr = GetWSMgr();
    if (!backgroundMgr || !wsMgr || !props?.currentBackgroundName || !curDoc) {
      return;
    }
    setBackgroundArea(getActiveBackgroundArea());
    return wsMgr.SubscribeToTopic(
      `BackgroundUpdate_${props.currentBackgroundName}`,
      doHandleBackgroundAreaChange
    );
  }, [backgroundMgr, curDoc, props.currentBackgroundName]);

  const doHandleBackgroundAreaChange = area => {
    setBackgroundArea(area);
  };

  const handleElementFocus = (area, layoutIndex) => {
    backgroundMgr.SetElementFocus(area, layoutIndex);
    ReRenderCanvas();
  };

  const handleBackgroundAreaChange = nextBackgroundArea => {
    if (!backgroundMgr) {
      return;
    }
    backgroundMgr.UpdateActiveBackground(nextBackgroundArea);
  };

  const handleChangeArea = area => blockArea => {
    const nextBackgroundArea = { ...backgroundAreaRef.current };
    nextBackgroundArea[area] = blockArea;

    handleBackgroundAreaChange(nextBackgroundArea);
  };

  const handleLayoutChange = area => layoutIndex => layout => {
    const nextBackgroundArea = { ...backgroundAreaRef.current };

    const nextArea = nextBackgroundArea[area];
    nextArea.SetLayout(layoutIndex, layout);

    handleElementFocus(area, layoutIndex);

    handleBackgroundAreaChange(nextBackgroundArea);
  };

  const handleLayoutAdd = (area, layout) => {
    const nextBackgroundArea = { ...backgroundAreaRef.current };

    if (!nextBackgroundArea[area]) {
      const newArea = new QBackgroundArea();
      newArea.InitBackgroundArea();
      nextBackgroundArea[area] = newArea;
    }

    const nextArea = nextBackgroundArea[area];
    const pinArea = enumBGAreas[area];

    layout.SetChartHorzCentering(pinArea.horz);
    layout.SetChartVertCentering(pinArea.vert);
    layout.SetPosition(0, 0);

    nextArea.AddLayout(layout);

    const areaLen = nextArea.GetLayouts().length;
    const nextIndex = areaLen - 1;

    handleElementFocus(area, nextIndex);

    handleBackgroundAreaChange(nextBackgroundArea);
  };

  const handleLayoutRemove = area => layoutIndex => () => {
    const nextBackgroundArea = { ...backgroundAreaRef.current };

    handleElementFocus(area, layoutIndex);
    // we gain focus here before its deleted.
    backgroundMgr.GainElementFocus();

    const nextArea = nextBackgroundArea[area];
    nextArea.RemoveLayoutAtIndex(layoutIndex);

    handleBackgroundAreaChange(nextBackgroundArea);
  };

  const handleInsertLayout = (area, layoutIndex, layout, oldArea, oldIndex) => {
    const nextBackgroundArea = { ...backgroundAreaRef.current };
    if (area !== backgroundMgr.DetermineLayoutArea(layout)) layout.SetDefaultBackgroundMargins();

    let prevArea = nextBackgroundArea[oldArea];
    if (prevArea && oldIndex != null) prevArea.RemoveLayoutAtIndex(oldIndex);

    let nextArea = nextBackgroundArea[area];
    if (!nextArea) {
      const newArea = new QBackgroundArea();
      newArea.InitBackgroundArea();
      newArea.AddLayout(layout);
      nextBackgroundArea[area] = newArea;
    } else {
      nextArea.InsertLayout(layout, layoutIndex);
    }

    handleElementFocus(area, layoutIndex ?? 0);

    handleBackgroundAreaChange(nextBackgroundArea);
  };

  const handleDropPhoto = pRef => {
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    const pm = doc?.GetPageMgr();
    const area = "topLeft";
    const maxWidth = Math.floor(pm?.GetMinPageWidth() * 0.25);
    let maxHeight = area?.includes("bottom") ? pm.GetFooterHeight() : pm.GetHeaderHeight();
    if (maxHeight < 24) maxHeight = 24;

    const dataRef = new QPicDesc();
    dataRef.fDirectory = pRef.fDirectory;
    dataRef.fFileName = pRef.fFileName;
    dataRef.maxHeight = maxHeight;
    dataRef.maxWidth = maxWidth;
    dataRef.scaleImage = true;

    const newLayout = wsMgr.GetNewLayoutByBoxSubType(enumBoxSubTypes.picture, { dataRef });
    handleLayoutAdd(area, newLayout);

    wsMgr.BroadcastTopic("expandBackgroundArea", area);
  };

  return {
    backgroundArea,
    handleDropPhoto,
    handleInsertLayout,
    handleLayoutAdd,
    handleLayoutRemove,
    handleLayoutChange,
    handleChangeArea,
    handleElementFocus
  };
};
